import {
  addTimezoneStartOfDay,
  addTimezoneEndOfDay,
} from '@spaced-out/ui-design-system/lib/utils';

import type {ByodFilterMap} from 'src/types/byod';


export type BuildByodGlobalPayloadArgs = {
  /**
   * The host's selected date window. `startDate` / `endDate` are plain calendar
   * dates (`YYYY-MM-DD`); they're stamped to the day's UTC boundaries in the
   * given `timezone`. Omit a bound to leave that boundary unset.
   */
  dateRange?: {
    startDate?: string;
    endDate?: string;
    timezone?: string;
  } | null;
  /**
   * Already-wire-shaped filters to scope the dashboard by (e.g.
   * `{ job_id: ['123'], office_id: ['LA'] }`). Spread in verbatim — the host
   * owns any value transforms. Dates from `dateRange` are stamped LAST and
   * overwrite any `start_date`/`end_date` placed here.
   */
  filters?: ByodFilterMap;
  /** Timezone override; defaults to `dateRange.timezone`, then `'UTC'`. */
  timezone?: string;
};

/**
 * Build the `globalPayload` a host passes to `<ByodDashboardView>`. The single
 * place the date/timezone + filter transform lives, so multiple embed consumers
 * don't each re-implement `addTimezoneStartOfDay/endOfDay`.
 *
 * Returns a fully-transformed `ByodFilterMap`: timezone-applied `start_date` /
 * `end_date` (when a `dateRange` is given) plus the consumer's filters (which
 * may be flat strings/lists or structured values). The embed treats the result
 * as opaque and injects it into every tile's `widget_filters`.
 */
export function buildByodGlobalPayload({
  dateRange,
  filters,
  timezone,
}: BuildByodGlobalPayloadArgs): ByodFilterMap {
  const payload: ByodFilterMap = {...(filters ?? {})};

  if (dateRange) {
    const tz = timezone || dateRange.timezone || 'UTC';
    if (dateRange.startDate) {
      payload.start_date = addTimezoneStartOfDay(dateRange.startDate, tz);
    }
    if (dateRange.endDate) {
      payload.end_date = addTimezoneEndOfDay(dateRange.endDate, tz);
    }
  }

  return payload;
}

/**
 * Defensive runtime check that an embed date window actually carries BOTH
 * bounds. The embed's `dateRange` prop is typed required, so this only ever
 * returns `false` when the type contract was bypassed (e.g. upstream URL/redux
 * state typed `string` but actually `undefined`/empty at runtime). The embed
 * uses it to raise a Sentry alert rather than silently issuing an unbounded
 * `/query` — there is intentionally NO fabricated default window.
 */
export function hasBoundedDateWindow(dateRange: {
  startDate?: string;
  endDate?: string;
}): boolean {
  return Boolean(dateRange.startDate) && Boolean(dateRange.endDate);
}
